import { useTranslation } from "@next-platform/client-common-core";
import { useCallback, useEffect, useState } from "react";

import readDiscoverRowScrollAvailability from "@/pages/Discover/readDiscoverRowScrollAvailability";

type UseDiscoverRowScrollParameters = {
  trackElement: HTMLDivElement | null;
  contentKey: string;
};

const useDiscoverRowScroll = ({ trackElement, contentKey }: UseDiscoverRowScrollParameters) => {
  const { t } = useTranslation();
  const [stateIsPreviousScrollAvailable, setStateIsPreviousScrollAvailable] = useState(false);
  const [stateIsNextScrollAvailable, setStateIsNextScrollAvailable] = useState(false);

  const updateScrollAvailability = useCallback(() => {
    if (trackElement === null) {
      setStateIsPreviousScrollAvailable(false);
      setStateIsNextScrollAvailable(false);
      return;
    }

    const availability = readDiscoverRowScrollAvailability({
      clientWidth: trackElement.clientWidth,
      scrollWidth: trackElement.scrollWidth,
      scrollLeft: trackElement.scrollLeft,
    });
    setStateIsPreviousScrollAvailable(availability.canScrollPrevious);
    setStateIsNextScrollAvailable(availability.canScrollNext);
  }, [trackElement]);

  useEffect(() => {
    updateScrollAvailability();
    if (trackElement === null) {
      return undefined;
    }

    trackElement.addEventListener("scroll", updateScrollAvailability, { passive: true });
    const resizeObserver = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateScrollAvailability);
    resizeObserver?.observe(trackElement);

    return () => {
      trackElement.removeEventListener("scroll", updateScrollAvailability);
      resizeObserver?.disconnect();
    };
  }, [contentKey, trackElement, updateScrollAvailability]);

  const handleScrollPrevious = useCallback(() => {
    if (trackElement === null) {
      return;
    }

    trackElement.scrollBy({ left: -trackElement.clientWidth, behavior: "smooth" });
  }, [trackElement]);

  const handleScrollNext = useCallback(() => {
    if (trackElement === null) {
      return;
    }

    trackElement.scrollBy({ left: trackElement.clientWidth, behavior: "smooth" });
  }, [trackElement]);

  return {
    canScrollPrevious: stateIsPreviousScrollAvailable,
    canScrollNext: stateIsNextScrollAvailable,
    handleScrollPrevious,
    handleScrollNext,
    previousLabel: t("common:back"),
    nextLabel: t("common:next"),
  };
};

export default useDiscoverRowScroll;
